import { CalendarClock, IndianRupee, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { superAdminAPI } from '../api/index';
import type { SystemSettings } from '../types/index';

export default function SuperAdminSettings() {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [trialDaysInput, setTrialDaysInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingPrice, setSavingPrice] = useState(false);
  const [savingTrial, setSavingTrial] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data } = await superAdminAPI.getSettings();
      setSettings(data);
      setPriceInput(data.subscription_price_rupees.toString());
      setTrialDaysInput(data.trial_period_days.toString());
    } catch (error) {
      console.error('Error fetching system settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrice = async () => {
    const rupees = parseFloat(priceInput);
    if (!Number.isFinite(rupees) || rupees <= 0) {
      toast.error('Enter a valid amount greater than 0');
      return;
    }

    try {
      setSavingPrice(true);
      const { data } = await superAdminAPI.updateSettings({ subscription_price_rupees: rupees });
      setSettings(data);
      setPriceInput(data.subscription_price_rupees.toString());
      toast.success('Subscription price updated for all agencies');
    } catch (error) {
      console.error('Error updating subscription price:', error);
      toast.error('Failed to update subscription price');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleSaveTrialDays = async () => {
    const days = parseInt(trialDaysInput, 10);
    if (!Number.isFinite(days) || days <= 0 || days > 365) {
      toast.error('Enter a whole number of days between 1 and 365');
      return;
    }

    try {
      setSavingTrial(true);
      const { data } = await superAdminAPI.updateSettings({ trial_period_days: days });
      setSettings(data);
      setTrialDaysInput(data.trial_period_days.toString());
      toast.success('Trial period updated — applies to new signups going forward');
    } catch (error) {
      console.error('Error updating trial period:', error);
      toast.error('Failed to update trial period');
    } finally {
      setSavingTrial(false);
    }
  };

  const priceChanged =
    settings != null && priceInput !== '' && parseFloat(priceInput) !== settings.subscription_price_rupees;

  const trialDaysChanged =
    settings != null && trialDaysInput !== '' && parseInt(trialDaysInput, 10) !== settings.trial_period_days;

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-gray-600">Loading settings...</div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-gray-600 mt-2">
            Configure global platform settings applied across all agencies
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
          {/* Subscription Pricing Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="rounded-lg bg-blue-50 p-2">
                <IndianRupee className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Monthly Subscription Charge</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              This is the amount every agency is billed per month once their trial ends. Changing it
              applies to all agencies going forward — existing invoices are not affected.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monthly price (₹)
            </label>
            <div className="relative mb-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-4 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="999.00"
              />
            </div>
            {settings && (
              <p className="text-xs text-gray-500 mb-6">
                Current price: <span className="font-semibold">{settings.formatted_price}</span> / month
              </p>
            )}

            <button
              onClick={handleSavePrice}
              disabled={savingPrice || !priceChanged}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {savingPrice ? 'Saving...' : 'Save changes'}
            </button>
          </div>

          {/* Trial Period Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="rounded-lg bg-purple-50 p-2">
                <CalendarClock className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Trial Period</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              How many days a new agency can use the platform free before their trial ends.
              Changing it only affects agencies that sign up after the change — existing trials keep
              their original end date.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trial length (days)
            </label>
            <div className="relative mb-2">
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                value={trialDaysInput}
                onChange={(e) => setTrialDaysInput(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 px-4 text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                placeholder="60"
              />
            </div>
            {settings && (
              <p className="text-xs text-gray-500 mb-6">
                Current trial period:{' '}
                <span className="font-semibold">{settings.trial_period_days} days</span> for new signups
              </p>
            )}

            <button
              onClick={handleSaveTrialDays}
              disabled={savingTrial || !trialDaysChanged}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {savingTrial ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

        {settings?.updated_at && (
          <p className="text-xs text-gray-500 mt-4 max-w-4xl">
            Settings last updated {new Date(settings.updated_at).toLocaleString('en-US')}
          </p>
        )}
      </div>
    </SuperAdminLayout>
  );
}
